import { FC, FormEvent, useEffect, useMemo, useState } from "react";
import { StateAction } from "../../helpers/functions/types";
import {
  CampaignBody,
  CampaignObjectDetectorEnum,
  CampaignType,
} from "@vviinn/user-managment-client-ts";

import { DetailedWrapper } from "../../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { SubSectionWithStyles } from "../../ui/layout";
import { transformFormData } from "../../helpers";
import { setSelectedFilters, useAppSelector } from "../../store";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";
import { GeneralSettings } from "./components/GeneralSettings";
import { CampaignTypeRadio } from "./components/CampaignTypeRadio";
import { PresetKey, UpdateCampaignBody } from "./types";
import { AdvancedVPDSettings } from "./components/AdvancedVPDSettings";
import { AdvancedNotVPDSettings } from "./components/AdvancedNotVPDSettings";
import { RelevantNets } from "../types";
import { handleCampaign } from "../../store/features/campaigns/handleCampaign";
import { handleBoosting } from "../boosting/handleBoosting";
import { useBoostingDraft } from "../boosting/hooks/useBoostingDraft";
import { BoostingFormFields } from "../boosting/components/BoostingFormFields";
import { useOutletContext } from "react-router";
import { CompanyAndSiteIds } from "../../routes/types";

export const NewCampaignForm: FC<{
  campaignType?: CampaignType | "BOOSTING";
  setCampaignType: StateAction<CampaignType | "BOOSTING" | undefined>;
  defaultPresetValues: CampaignBody;
  relevantNets: RelevantNets;
}> = ({ campaignType, setCampaignType, defaultPresetValues, relevantNets }) => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();
  const [currentValues, setCurrentValues] =
    useState<CampaignBody>(defaultPresetValues);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [campaignPreset, setCampaignPreset] = useState<PresetKey>("default");
  const [referenceCampaignId, setReferenceCampaignId] = useState<string>();
  const [objectDetectorValue, setObjectDetectorValue] = useState<
    CampaignObjectDetectorEnum | undefined
  >(defaultPresetValues?.objectDetector);

  const { selectedFilters } = useAppSelector((state) => state.ui.campaigns);
  const campaigns = useAppSelector((state) => state.campaigns.campaigns);

  const boostingDraft = useBoostingDraft(siteId);

  const handlerParams = useRequestHandlers();
  const { dispatch } = handlerParams;
  const { createCampaign } = handleCampaign(handlerParams, {
    companyId,
    siteId,
  });
  const { handleUpsertBoosting } = handleBoosting(handlerParams, {
    companyId,
    siteId,
  });

  useEffect(() => {
    setCurrentValues({ ...defaultPresetValues });
    setCampaignPreset("default");
    setReferenceCampaignId(undefined);
    setShowAdvancedSettings(false);
  }, [defaultPresetValues]);

  useEffect(() => {
    const referenceCampaign =
      !!referenceCampaignId &&
      campaigns.find(({ id }) => id === referenceCampaignId);
    if (referenceCampaign) {
      setCurrentValues({
        ...referenceCampaign,
        ...(referenceCampaign.type === "VPD"
          ? referenceCampaign.colorParameters //color parameters is object for Campaign, but 3 separate entries for CampaignBody
          : {}),
      });
    } else setCurrentValues(defaultPresetValues);
  }, [referenceCampaignId]);

  const backLink = `/company/${companyId}/site/${siteId}/campaigns`;
  const serviceCampaignType =
    campaignType !== "BOOSTING" ? campaignType : undefined;

  const handleFormSubmit = async (e: FormEvent) => {
    if (campaignType === "BOOSTING") {
      if (!boostingDraft.isValid) return undefined;
      const result = await handleUpsertBoosting(boostingDraft.payload);
      if (result && result.id) {
        return `/company/${companyId}/site/${siteId}/campaigns/boosting/${result.id}`;
      }
      return undefined;
    }

    if (!defaultPresetValues || !campaignType || !companyId || !siteId) return;

    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const campaignBody = transformFormData<CampaignBody>(data);

    const campaignData = {
      ...currentValues,
      ...campaignBody,
      id: undefined,
      linkedCategories: undefined,
    }; //prevent copy id due to duplicating campaign, also linking categories don't work for all campaigns except text search and crash the text search

    const requestResult = await createCampaign(campaignType, {
      ...campaignData,
    });

    if (requestResult) {
      const campaignCredentials = requestResult as CampaignBody;
      if (selectedFilters.length && !selectedFilters.includes(campaignType))
        dispatch(setSelectedFilters({ feature: "campaigns", value: [] }));
      return `/company/${companyId}/site/${siteId}/campaigns/service/${campaignCredentials.id}`;
    }
  };

  const canBeConfigured = useMemo(
    () => showAdvancedSettings && companyId && siteId,
    [showAdvancedSettings, companyId, siteId],
  );

  const updateBodyField: UpdateCampaignBody = (updateData) => {
    setCurrentValues((prevValues) => ({ ...prevValues, ...updateData }));
  };

  return (
    <DetailedWrapper
      createItemAction={handleFormSubmit}
      detailedPage="campaign"
      detailedPageType="new"
      backLink={backLink}
    >
      {campaignType !== "BOOSTING" && (
        <GeneralSettings
          campaignType={campaignType}
          setCampaignType={setCampaignType}
          currentValues={currentValues}
          updateBodyField={updateBodyField}
          presetData={{
            campaignPreset: campaignPreset,
            onPresetChange: (value: string) => {
              setCampaignPreset(value as PresetKey);
              setShowAdvancedSettings(value === "custom");
            },
            referenceCampaignId: referenceCampaignId,
            setReferenceCampaignId: setReferenceCampaignId,
          }}
          showAdvancedSettings={showAdvancedSettings}
          setShowAdvancedSettings={setShowAdvancedSettings}
        />
      )}
      {campaignType === "BOOSTING" && (
        <SubSectionWithStyles title="Boosting Configuration" isSubHeader={false}>
          <BoostingFormFields
            draft={boostingDraft}
            nameLabel="Campaign Name"
            belowName={
              <CampaignTypeRadio
                campaignType={campaignType}
                setCampaignType={setCampaignType}
              />
            }
          />
        </SubSectionWithStyles>
      )}
      {canBeConfigured &&
        serviceCampaignType &&
        (serviceCampaignType === "VPD" ? (
          <AdvancedVPDSettings
            currentValues={currentValues}
            updateBodyField={updateBodyField}
            siteId={siteId}
            companyId={companyId}
          />
        ) : (
          <AdvancedNotVPDSettings
            campaignType={serviceCampaignType}
            currentValues={currentValues}
            updateBodyField={updateBodyField}
            objectDetectorValue={objectDetectorValue}
            setObjectDetectorValue={setObjectDetectorValue}
            relevantNets={relevantNets}
          />
        ))}
    </DetailedWrapper>
  );
};
